import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
    fetchBins,
    flushInventoryItemsQueue,
    isSpecialBin,
    parseBinString, processFailedSaveResponse, processRequestItem,
    processSaveReportResponse,
    saveReport,
    SaveInventoryItem,
    SaveInventoryRequestItem,
    submitReport
} from '../../utils'
import BinEdit from './BinEdit'
import BinSummary from './BinSummary'
import {DeserializeItemEvent, webEvents} from '../../../common/WebEvents'
import Notifications, {Notification} from '../../../common/Notifications'
import {Box} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import excludedSound from '../../../assets/sounds/excluded.mp3'
import {NotificationSnackbar} from '../../../common/ui'
import {useDispatch} from 'react-redux'
import { createError } from '../../../store/slices/error'

type BinSummaryTotals = {
    totalBins: number
    totalSerials: number
    totalNonSerials: number
    totalExcluded: number
    totalSaved: number
    row: string
}

interface BinTotals {
    serials: number
    nonSerials: number
    excludes: number
}

function ByBin() {
    const [serialItems, setSerialItems] = useState<SaveInventoryItem[]>([])
    const [nonSerialItems, setNonSerialItems] = useState<SaveInventoryItem[]>([])
    const [isSaving, setIsSaving] = useState(false)
    const [pendingSync, setPendingSync] = useState(false)
    const [pendingRequestItems, setPendingRequestItems] = useState<SaveInventoryRequestItem[] | null>([])
    const [pendingResponse, setPendingResponse] = useState<{items: SaveInventoryItem[], isSync: boolean} | null>(null)
    const [processQueue, setProcessQueue] = useState<boolean>(false)
    const [failedSaveReportResponse, setFailedSaveReportResponse] = useState<boolean>(false)
    const [isWebEventsConnected, setIsWebEventsConnected] = useState<boolean>(false)

    const [activeIter] = useState(() => {
        const storageItem = window.sessionStorage.getItem('activeIter')
        return storageItem ? JSON.parse(storageItem) : null
    })

    const [deserializedNotifications, setDeserializedNotifications] = useState<
        Notification[]
    >([])
    const [currentBin, setCurrentBin] = useState<string | null>(null)
    const [allBins, setAllBins] = useState<string[]>([])
    const [nextBin, setNextBin] = useState<string | null>(null)
    const [prevBin, setPrevBin] = useState<string | null>(null)
    const [row, setRow] = useState<string>('')
    const [binSummaryTotals, setBinSummaryTotals] = useState<BinSummaryTotals>({
        totalSaved: 0,
        totalNonSerials: 0,
        totalExcluded: 0,
        totalBins: 0,
        totalSerials: 0,
        row: ''
    })
    const [entryErrors, setEntryErrors] = useState<string | null>(null)
    const dispatch = useDispatch()

    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const navigate = useNavigate()

    const processSaveReport = useCallback((requestItems: SaveInventoryRequestItem[] | null) => {
        let isSync = requestItems === null

        setIsSaving(true)
        saveReport(activeIter?.countId, requestItems)
            .then(items => setPendingResponse({items, isSync}))
            .catch(error => {
                setIsSaving(false)
                webEvents.error(error.message, {iteration: activeIter}, error.stack)
                playAudio()
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
                setFailedSaveReportResponse(true)
            })
    }, [activeIter])

    function playAudio() {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    useEffect(() => {
        if (isWebEventsConnected)
            return

        if (!activeIter) {
            navigate('/inventory/counts')
            return
        }
        processSaveReport(null)
        fetchBins().then(setAllBins)

        webEvents.mergeOptions({
            onSyncReport: () => {
                if (isSaving)
                    setPendingSync(true)
                else
                    processSaveReport(null)
            },
            onDeserializeItem: (event: DeserializeItemEvent) => {
                if (isSaving)
                    setPendingSync(true)
                else
                    processSaveReport(null)

                setDeserializedNotifications(prevNotifications => [
                    ...prevNotifications,
                    {sku: event.sku}
                ])
            }
        })

        setIsWebEventsConnected(true)
    }, [])

    // aggregate bin data
    const {binMap, totals} = useMemo(() => {
        let map: Record<string, BinTotals> = {}
        let totals: BinSummaryTotals = {
            totalBins: 0,
            totalSerials: 0,
            totalNonSerials: 0,
            totalExcluded: 0,
            totalSaved: 0,
            row: ''
        }

        const calculateBinTotals = (reportItems: SaveInventoryItem[]) => {
            for (let item of reportItems) {
                let bin = item.bin as string
                let binParts = bin.split(' ')
                let row = binParts[0]
                totals.row = row

                if (binParts.length == 3) row = `${binParts[0]} ${binParts[1]}`

                if (!(bin in map)) {
                    totals.totalBins++
                    map[bin] = {serials: 0, nonSerials: 0, excludes: 0}
                }

                if (item.excluded) {
                    map[bin].excludes++
                    totals.totalExcluded++
                } else if (item.serial) {
                    map[bin].serials++
                    totals.totalSerials++
                } else {
                    map[bin].nonSerials += item.quantity
                    totals.totalNonSerials += item.quantity
                }

         }
        }

        calculateBinTotals(serialItems)
        calculateBinTotals(nonSerialItems)
        totals.totalSaved = totals.totalSerials + totals.totalNonSerials

        return {binMap: map, totals}
    }, [serialItems, nonSerialItems])

    useEffect(() => {
        setBinSummaryTotals(totals)
    }, [totals])

    useEffect(() => {
        if (!currentBin || allBins.length === 0) {
            setNextBin(null)
            setPrevBin(null)
            return
        }

        let sortedBins = [...allBins].sort((a, b) => {
            let aIsSpecial = isSpecialBin(a)
            let bIsSpecial = isSpecialBin(b)

            /* sort special bins separately */
            if (aIsSpecial && bIsSpecial) return a.localeCompare(b)

            /* force special bins to the top of the list */
            if (aIsSpecial) return -1
            if (bIsSpecial) return 1

            return a.localeCompare(b)
        })

        let currentBinName = currentBin.startsWith('01') || isSpecialBin(currentBin) ? currentBin : `01 ${currentBin}`
        let currentIndex = sortedBins.indexOf(currentBinName)
        setNextBin(
            currentIndex + 1 < sortedBins.length
                ? sortedBins[currentIndex + 1]
                : null
        )
        setPrevBin(currentIndex - 1 >= 0 ? sortedBins[currentIndex - 1] : null)
        setRow(parseBinString(currentBinName).row)
    }, [currentBin, allBins])

    const handleBackToSummary = () => {
        setCurrentBin(null)
    }

    useEffect(() => {
        if (pendingRequestItems === null || pendingRequestItems.length)
            processSaveReport(pendingRequestItems)
    }, [pendingRequestItems, processSaveReport])

    useEffect(() => {
        if (!processQueue)
            return

        let result = flushInventoryItemsQueue(serialItems, nonSerialItems)

        if (result.serials)
            setSerialItems(result.serials)
        if (result.nonSerials)
            setNonSerialItems(result.nonSerials)

        setPendingRequestItems(result.requestItems ?? [])
        setProcessQueue(false)
    }, [processQueue, serialItems, nonSerialItems])

    useEffect(() => {
        if (pendingResponse === null)
            return

        let {serials, nonSerials} = processSaveReportResponse(
            pendingResponse.items, serialItems, nonSerialItems, pendingResponse.isSync)

        if (serials)
            setSerialItems(serials)
        if (nonSerials)
            setNonSerialItems(nonSerials)

        setIsSaving(false)
        setPendingResponse(null)

        if (pendingResponse.isSync) {
            setPendingSync(false)
            setPendingRequestItems([])
        }
        else if (pendingSync) {
            setPendingSync(false)
            setPendingRequestItems(null)
        } else {
            setProcessQueue(true)
        }
    }, [pendingResponse, serialItems, nonSerialItems, pendingSync])

    useEffect(() => {
        if (failedSaveReportResponse) {
            const {
                updatedSerialItems,
                updatedNonSerialItems
            } = processFailedSaveResponse(serialItems, nonSerialItems)
            setSerialItems(updatedSerialItems)
            setNonSerialItems(updatedNonSerialItems)
            setProcessQueue(true)
        }
        return () => setFailedSaveReportResponse(false)
    }, [failedSaveReportResponse, nonSerialItems, serialItems])

    function saveRequestItem(reqItem: SaveInventoryRequestItem) {
        let result = processRequestItem(reqItem, serialItems, nonSerialItems, isSaving || pendingSync)

        if (result === null) {
            playAudio()

            if (reqItem.serial)
                setEntryErrors(`Serial Item ${reqItem.serial} already added`)
            else
                setEntryErrors(`SKU Item ${reqItem.sku} already added`)
        } else {
            if (result.serials)
                setSerialItems(result.serials)

            if (result.nonSerials)
                setNonSerialItems(result.nonSerials)

            if (result.progress && !result.progress.startsWith('q-'))
                setPendingRequestItems([reqItem])
        }
    }

    function processSubmitReport() {
        submitReport(activeIter.countId)
            .then(() => {
                webEvents.info('Report submitted successfully', {
                    iteration: activeIter
                })
                window.sessionStorage.removeItem('activeIter')
            })
            .catch(error => {
                webEvents.error(
                    error.message,
                    {iteration: activeIter},
                    error.stack
                )
                playAudio()
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
    }

    return (
        <>
            {entryErrors &&
                <NotificationSnackbar
                    open={true}
                    message={entryErrors}
                    onClose={() => setEntryErrors(null)}
                    severity="error"
                    autoHideDuration={3000}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                />
            }
            <Box position="fixed" top={30} right={80} zIndex={50}>
                <Notifications notificationList={deserializedNotifications}/>
            </Box>
            <audio ref={audioPlayer} src={excludedSound} />
            {currentBin ? (
                <BinEdit
                    serialItems={serialItems}
                    nonSerialItems={nonSerialItems}
                    saveRequestItem={saveRequestItem}
                    currentBin={currentBin}
                    onSetCurrentBin={setCurrentBin}
                    onBackToSummary={handleBackToSummary}
                    nextBin={nextBin}
                    prevBin={prevBin}
                    row={row}
                />
            ) : (
                <BinSummary
                    binMap={binMap}
                    totals={binSummaryTotals}
                    currentBin={currentBin}
                    onSetCurrentBin={setCurrentBin}
                    row={row}
                    allBins={allBins}
                    processSubmitReport={processSubmitReport}
                />
            )}
        </>
    )
}

export default ByBin
